import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/site/Reveal";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Mesajınız gönderildi!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="bg-[#0A1A10] sr-grain pt-36 pb-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <span className="font-mono-accent text-xs uppercase tracking-widest text-[#D4AF37]">İletişim</span>
          <h1 className="font-serif-display text-white text-4xl sm:text-5xl font-bold mt-3">Bize Ulaşın</h1>
          <p className="text-white/60 mt-4 max-w-lg">Soru, öneri veya iş birliği talepleriniz için formu doldurun. En kısa sürede dönüş yapacağız.</p>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid gap-10 md:grid-cols-5">
          <div className="md:col-span-2 space-y-6">
            {settings.contact_email && (
              <div className="flex items-start gap-4"><div className="grid place-items-center h-11 w-11 rounded-xl bg-[#1B4D2E] text-[#D4AF37]"><Mail className="h-5 w-5" /></div><div><p className="font-mono-accent text-xs uppercase tracking-wider text-[#5C6F62]">E-posta</p><p className="text-[#1A251E] font-medium">{settings.contact_email}</p></div></div>
            )}
            {settings.contact_phone && (
              <div className="flex items-start gap-4"><div className="grid place-items-center h-11 w-11 rounded-xl bg-[#1B4D2E] text-[#D4AF37]"><Phone className="h-5 w-5" /></div><div><p className="font-mono-accent text-xs uppercase tracking-wider text-[#5C6F62]">Telefon</p><p className="text-[#1A251E] font-medium">{settings.contact_phone}</p></div></div>
            )}
            {settings.contact_address && (
              <div className="flex items-start gap-4"><div className="grid place-items-center h-11 w-11 rounded-xl bg-[#1B4D2E] text-[#D4AF37]"><MapPin className="h-5 w-5" /></div><div><p className="font-mono-accent text-xs uppercase tracking-wider text-[#5C6F62]">Adres</p><p className="text-[#1A251E] font-medium">{settings.contact_address}</p></div></div>
            )}
          </div>

          <div className="md:col-span-3">
            {done ? (
              <div data-testid="contact-success" className="rounded-3xl bg-white border border-[#E2E8E4] p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-[#40916C] mx-auto" />
                <h3 className="font-serif-display text-2xl font-semibold text-[#1A251E] mt-5">Teşekkürler!</h3>
                <p className="text-[#5C6F62] mt-2">Mesajınız bize ulaştı. En kısa sürede dönüş yapacağız.</p>
                <button onClick={() => setDone(false)} className="mt-6 text-[#40916C] font-medium underline" data-testid="contact-new-btn">Yeni mesaj gönder</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white border border-[#E2E8E4] p-8 space-y-5">
                <Field label="Ad Soyad" testid="contact-name">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className="sr-input" placeholder="Adınız Soyadınız" />
                </Field>
                <Field label="E-posta" testid="contact-email">
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className="sr-input" placeholder="ornek@eposta.com" />
                </Field>
                <Field label="Mesaj" testid="contact-message">
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" className="sr-input resize-none" placeholder="Mesajınızı yazın..." />
                </Field>
                <button disabled={loading} data-testid="contact-submit-btn" className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#0F2F1D] text-white font-semibold hover:bg-[#1B4D2E] transition-colors disabled:opacity-60">
                  {loading ? "Gönderiliyor..." : <>Gönder <Send className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1A251E] mb-2">{label}</span>
      {children}
    </label>
  );
}
